/**
 * Cost-of-Error — sheet 8 § "Read Accumulation and Cost of Being Wrong."
 *
 * Each exploit has a different cost-of-error. Cheap exploits can be
 * applied liberally on weak reads; expensive exploits require strong
 * evidence because being wrong costs a stack. This quiz tests
 * cost-classification across the canonical exploits.
 *
 * 3-option answer space: Cheap / Medium / Expensive.
 */

import { pickFrom } from "@/features/quiz/lib/common";

export type CostTier = "cheap" | "medium" | "expensive";

export const COST_LABEL: Record<CostTier, string> = {
  cheap: "Cheap (low evidence)",
  medium: "Medium (some evidence)",
  expensive: "Expensive (strong read needed)",
};

export interface CostQuestion {
  exploit: string;
  cost: CostTier;
  rationale: string;
}

export const COST_OF_ERROR_BANK: CostQuestion[] = [
  {
    exploit: "Bluff less vs a station.",
    cost: "cheap",
    rationale: "Sheet 8: low-evidence exploit. If wrong (they weren't really a station), you miss one bluff that would've worked, a small cost. If right, you stop donating to their call-down range.",
  },
  {
    exploit: "Value bet thinner against a perceived calling station.",
    cost: "cheap",
    rationale: "Low evidence required. If wrong, you lose one extra bet sometimes. If right, you earn extra value across many hands. Apply liberally even on thin reads.",
  },
  {
    exploit: "Bluff more against a nit.",
    cost: "medium",
    rationale: "Sheet 8 grades this medium-high. If wrong (they weren't a real nit), they have it and you lose a stack to a bluff into a value-only range. Requires multiple confirming actions.",
  },
  {
    exploit: "Hero-call wider against a perceived maniac on the river.",
    cost: "expensive",
    rationale: "Sheet 8: HIGH evidence required. If wrong, you pay off a real hand for a full stack. Don't hero-call on one wild-looking bet. Wait for multiple confirmed light-bluff showdowns first.",
  },
  {
    exploit: "Fold strong made hands to a nit's sustained aggression.",
    cost: "medium",
    rationale: "If wrong, you fold the best hand and lose the pot (not a stack). Lower cost than bluffing-into-them. Medium evidence: usually a couple confirming actions plus a clear sustained-aggression line.",
  },
  {
    exploit: "Trap with monsters (slow-play) against a maniac.",
    cost: "medium",
    rationale: "If wrong (they weren't really a maniac), you give them a free card to outdraw. If right, you stack them when they bluff into your nuts. Medium cost. Wait for confirmed aggression frequency before trapping.",
  },
  {
    exploit: "Size up value bets against a station.",
    cost: "cheap",
    rationale: "Cheap: if you're wrong about them being a station, they fold to the bigger size and you miss one call. If right, you extract more across many hands. Same risk profile as 'bluff less.'",
  },
  {
    exploit: "4-bet light preflop against a perceived maniac.",
    cost: "expensive",
    rationale: "Maniacs 5-bet light too. If wrong on the read OR if they happen to have AA this orbit, the 4-bet bluff costs ~25bb. Demands strong evidence the 3-bet range is wide AND the 5-bet range is reasonable.",
  },
  {
    exploit: "3-bet light against a nit's open.",
    cost: "cheap",
    rationale: "Nits open tight and fold a lot to 3-bets. If wrong, you lose one 3-bet preflop (small cost). If right, you steal a lot of dead money. Apply liberally once you have a nit read.",
  },
  {
    exploit: "Refuse to fold an overpair on a wet board vs a limper-whale who just check-raised the turn.",
    cost: "expensive",
    rationale: "Limp-whales rarely check-raise turns. When they do, they usually have it (sets, two pair, made hand). If wrong (they're spazzing), you call off a stack. Requires very strong 'this person never check-raise-bluffs' read.",
  },
];

export function generateQuestion(
  rand: () => number = Math.random,
): CostQuestion {
  return pickFrom(COST_OF_ERROR_BANK, rand);
}

export const _internals = { COST_OF_ERROR_BANK };
